import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';

import cn from 'classnames';
import { clearHiddenFieldValues } from 'helpers/field-config/form-page-helpers';
import { getCheckTypeIcon } from 'helpers/iconsHelper';
import { Controller, useFieldArray, useForm, useWatch } from 'react-hook-form';
import { toast } from 'react-hot-toast';

import { CandidateQuestionnaireSelectorDetails } from './components/CandidateQuestionnaireSelectorDetails/CandidateQuestionnaireSelectorDetails';
import { CustomReferenceCheckDetails } from './components/CustomReferenceCheckDetails/CustomReferenceCheckDetails';
import { DocumentSigningSelectorDetails } from './components/DocumentSigningSelectorDetails/DocumentSigningSelectorDetails';
import { ReferenceSelectorDetails } from './components/ReferenceSelectorDetails/ReferenceSelectorDetails';
import FieldConfigRenderer from 'components/FieldConfigRenderer/FieldConfigRenderer';
import { InputGroup, Select, ToggleSwitchInput } from 'components/FormComponents';
import { CustomToast } from 'components/Toast/CustomToast';

import styles from './ChecksSelector.module.scss';

const MISSING_REQUIRED_FIELDS_TITLE =
  "You're missing a required field. Please complete all required fields before proceeding.";

export const SelectorTypes = {
  REFERENCE: 'reference',
  CUSTOM_REFERENCE: 'custom_reference',
  QUESTIONNAIRE: 'candidate_questionnaire',
  DOCUMENT_SIGNING: 'document_signing',
  BACKGROUND_CHECKS: 'background_checks',
};

export const ChecksSelector = forwardRef(
  (
    {
      company,
      companyChecks = [],
      existingChecks = [],
      form,
      forms,
      targetForm,
      brands,
      bundles,
      type,
      bulk,
      className,
      noVolunteerUpdate = false,
      useDocuments = false,
      allowUploads = false,
      getBrandUploads = () => {},
      setValidity = () => {},
      onChange = () => {},
      onSubmit = () => {},
      getUpload = () => {},
      signUpload = () => {},
      createUpload = () => {},
      deleteUpload = () => {},
      singleType = false,
      candidateData = null,
    },
    ref,
  ) => {
    const {
      control,
      register,
      watch,
      reset,
      setValue,
      getValues,
      setError,
      clearErrors,
      unregister,
      formState: { errors },
      handleSubmit,
    } = useForm();

    const { fields: checks, append, remove } = useFieldArray({ control, name: 'checks' });

    const values = useWatch({ control });

    const bundleId = useWatch({ control, name: 'candidate.bundle_id' });
    const brandId = useWatch({ control, name: 'candidate.brand_id' });
    const documentId = useWatch({ control, name: 'candidate.document_id' });

    const [enabledChecks, setEnabledChecks] = useState({});
    const [documentsEnabled, setDocumentsEnabled] = useState(false);
    const [documents, setDocuments] = useState([]);

    const isSettingBundleRef = useRef(false);
    const enabledCompanyChecks = useMemo(
      () => Object.fromEntries(companyChecks.filter(cc => !cc.archived).map(cc => [cc.type, true])),
      [companyChecks],
    );

    const companyCheckConfigurations = useMemo(
      () =>
        Object.fromEntries(
          companyChecks
            .filter(cc => !cc.archived)
            .map(cc => {
              const cfg = cc.configuration;
              const obj = cfg && typeof cfg === 'object' && !Array.isArray(cfg) ? cfg : {};
              return [cc.type, obj];
            }),
        ),
      [companyChecks],
    );

    // Set bundle values for selected bundle
    useEffect(() => {
      if (!bundleId) {
        isSettingBundleRef.current = false;
        return;
      }
      const bundle = bundles.find(bundle => bundle.id === bundleId);
      if (!bundle) return;

      const checksData = bundle.checks_data || [];

      isSettingBundleRef.current = true;
      setValue('checks', checksData);
      // Explicitly set each check's details path so form state has checks.i.details.key for every
      // field; bundle details then behave like existing values in AdminCompanyCheck/CandidateCompletionModal.
      checksData.forEach((check, i) => {
        const details = check.details || {};
        Object.keys(details).forEach(key => {
          setValue(`checks.${i}.details.${key}`, details[key]);
        });
      });

      const volunteer = bundle.volunteer;
      setValue('candidate.volunteer', volunteer);

      const brand = brands.find(brand => brand.id === bundle.brand_id);
      if (!brand) {
        toast.custom(t => (
          <CustomToast
            t={t}
            title="Bundle brand is invalid. We will use the default brand instead."
            type="error"
          />
        ));
      } else {
        setValue('candidate.brand_id', brand.id);
      }

      // Reset flag after checks have updated
      setTimeout(() => {
        isSettingBundleRef.current = false;
      }, 100);
    }, [bundleId, brands?.length, companyChecks, company, candidateData, bundles, setValue]);

    // Get brand documents
    useEffect(() => {
      if (!brandId || !useDocuments) {
        setDocuments([]);
        return;
      }

      const brand = brands.find(brand => brand.id === brandId);
      if (!brand?.documents_active) {
        setDocuments([]);
        return;
      }

      getBrandUploads(brandId).then(({ payload }) => {
        setDocuments(payload.uploads);
      });
    }, [brandId, useDocuments, brands?.length]);

    const runChecksFormValidation = useCallback(
      (onValid, onInvalid) => {
        const clearedFieldPaths = [];
        checks.forEach((check, index) => {
          const companyCheck = companyChecks.find(cc => cc.type === check.type);
          const checkType = companyCheck?.check_type;
          if (checkType?.client_fields?.length > 0) {
            const companyCheckConfig = companyCheck?.configuration ?? null;
            const cleared = clearHiddenFieldValues(
              checkType.client_fields,
              getValues,
              setValue,
              `checks.${index}.details`,
              {
                company: company ?? null,
                enabledCompanyChecks,
                companyCheckConfigurations,
                companyCheckConfig,
                candidate: candidateData ?? null,
                candidateValues: null,
                checkDetails: null,
                subCheckDetails: null,
                selectedOptionsContext: {},
              },
            );
            clearedFieldPaths.push(...cleared);
          }
        });

        if (clearedFieldPaths.length > 0) {
          unregister(clearedFieldPaths);
        }

        setTimeout(() => {
          clearedFieldPaths.forEach(path => clearErrors(path));
          handleSubmit(onValid, onInvalid)();
        }, 0);
      },
      [
        checks,
        companyChecks,
        getValues,
        setValue,
        unregister,
        clearErrors,
        handleSubmit,
        company,
        enabledCompanyChecks,
        companyCheckConfigurations,
        candidateData,
      ],
    );

    useImperativeHandle(
      ref,
      () => ({
        submit: () => {
          runChecksFormValidation(
            data => {
              delete data.checks[-1];
              onSubmit(data);
            },
            submitErrors => {
              if (submitErrors && Object.keys(submitErrors).length > 0) {
                toast.custom(t => (
                  <CustomToast t={t} title={MISSING_REQUIRED_FIELDS_TITLE} type="error" />
                ));
              }
            },
          );
        },
        validate: () =>
          new Promise(resolve => {
            runChecksFormValidation(
              () => resolve(true),
              submitErrors => {
                if (submitErrors && Object.keys(submitErrors).length > 0) {
                  toast.custom(t => (
                    <CustomToast t={t} title={MISSING_REQUIRED_FIELDS_TITLE} type="error" />
                  ));
                }
                resolve(false);
              },
            );
          }),
        reset: values => {
          reset(values);
        },
      }),
      [runChecksFormValidation, onSubmit, reset],
    );

    // Clear Bundle
    const clearBundle = () => {
      reset({ checks: [], brand_id: null, volunteer: false });
    };

    // Set enabled checks & clear bundle
    useEffect(() => {
      let enabledChecks = {};

      if (singleType && checks.length > 1) {
        const newCheck = checks[1];
        enabledChecks[newCheck.type] = 0;
        setValue('checks', [newCheck]);
      } else {
        checks.forEach((check, index) => {
          if (check?.type) {
            enabledChecks[check.type] = index;
          }
        });
        setEnabledChecks(enabledChecks);
      }

      // Don't clear bundle if we're currently setting it
      if (isSettingBundleRef.current) return;
      if (!bundleId) return;
      const bundle = bundles.find(bundle => bundle.id === bundleId);
      if (!bundle) return;

      // Compare check types, not just length
      const bundleCheckTypes = (bundle.checks_data || [])
        .map(c => c?.type)
        .filter(Boolean)
        .sort();
      const currentCheckTypes = checks
        .map(c => c?.type)
        .filter(Boolean)
        .sort();
      const typesMatch =
        bundleCheckTypes.length === currentCheckTypes.length &&
        bundleCheckTypes.every((type, idx) => type === currentCheckTypes[idx]);

      if (!typesMatch) {
        setValue('candidate.bundle_id', null);
      }
    }, [checks, bundleId, bundles, setValue]);

    // Set validity
    useEffect(() => {
      setValidity(checks.length > 0);
    }, [checks?.length]);

    // OnChange
    useEffect(() => {
      onChange(values);
    }, [values]);

    const isExistingCheck = useCallback(
      check => {
        const existingCheck = existingChecks.find(
          existingCheck => existingCheck.type === check.type,
        );
        return existingCheck?.completed_at === null && !existingCheck?.canceled;
      },
      [existingChecks],
    );

    const getExistingCheck = useCallback(
      check => {
        return existingChecks.find(existingCheck => existingCheck.type === check.type);
      },
      [existingChecks],
    );

    const renderDetails = companyCheck => {
      if (!companyCheck.type || companyCheck.archived) return null;

      switch (companyCheck.type) {
        case 'reference':
          return (
            <ReferenceSelectorDetails
              key={companyCheck.type}
              index={enabledChecks.reference}
              append={append}
              remove={remove}
              register={register}
              watch={watch}
              credits={companyCheck?.credits}
              setValue={setValue}
              form={form}
              forms={forms}
              type={type}
              bulk={bulk}
              targetForm={targetForm}
              documents={documents}
              signUpload={signUpload}
              createUpload={createUpload}
              handleDeleteUpload={deleteUpload}
              allowUploads={allowUploads}
            />
          );
        case 'custom_reference':
          return (
            <CustomReferenceCheckDetails
              key={companyCheck.type}
              index={enabledChecks.custom_reference}
              append={append}
              remove={remove}
              register={register}
              control={control}
              watch={watch}
              setValue={setValue}
              credits={companyCheck?.credits}
              existingCheck={getExistingCheck(companyCheck)}
              bulk={bulk}
              documents={documents}
              forms={forms}
              signUpload={signUpload}
              createUpload={createUpload}
              handleDeleteUpload={deleteUpload}
              allowUploads={allowUploads}
            />
          );
        case 'candidate_questionnaire':
          return (
            <CandidateQuestionnaireSelectorDetails
              key={companyCheck.type}
              index={enabledChecks.candidate_questionnaire}
              append={append}
              remove={remove}
              control={control}
              register={register}
              setValue={setValue}
              setError={setError}
              clearErrors={clearErrors}
              watch={watch}
              credits={companyCheck?.credits}
              candidateForms={(forms.questionnaires || []).filter(form => !form.archived)}
            />
          );
        case 'document_signing':
          return (
            <DocumentSigningSelectorDetails
              key={companyCheck.type}
              index={enabledChecks.document_signing}
              documents={documents}
              watch={watch}
              register={register}
              append={append}
              remove={remove}
              setValue={setValue}
              control={control}
              getUpload={getUpload}
              signUpload={signUpload}
              createUpload={createUpload}
              deleteUpload={deleteUpload}
              credits={companyCheck?.credits}
            />
          );
        default:
          return (
            <SelectorDetails
              key={companyCheck.type}
              company={company}
              enabledCompanyChecks={enabledCompanyChecks}
              companyCheckConfigurations={companyCheckConfigurations}
              companyCheck={companyCheck}
              candidateData={candidateData}
              bulk={bulk}
              index={enabledChecks[companyCheck.type]}
              isExistingCheck={isExistingCheck(companyCheck)}
              control={control}
              errors={errors}
              append={append}
              remove={remove}
              watch={watch}
              register={register}
              setValue={setValue}
              getValues={getValues}
            />
          );
      }
    };

    return (
      <form className={cn(styles.root, { [className]: className })}>
        <div className="p-6 pb-0 flex flex-col">
          {bundles?.length > 0 && (
            <div className="card card-with-border u-padding u-margin-bottom">
              <InputGroup title="Bundle" className="u-margin-bottom--0">
                <Select
                  name="candidate.bundle_id"
                  register={register}
                  value={bundleId}
                  placeholder="Select Bundle..."
                  useSearch>
                  {bundles.map(bundle => (
                    <Select.Item key={bundle.id} value={bundle.id}>
                      {bundle.name}
                    </Select.Item>
                  ))}
                </Select>
                {bundleId && (
                  <a className={styles.clear_bundle} onClick={clearBundle}>
                    Clear Bundle
                  </a>
                )}
              </InputGroup>
            </div>
          )}

          {company?.use_volunteers && !noVolunteerUpdate && (
            <Controller
              control={control}
              name="candidate.volunteer"
              render={({ field: { onChange, value } }) => (
                <ToggleSwitchInput
                  name="candidate.volunteer"
                  subtext="Candidate won't be a paid employee of the company"
                  text="Volunteer"
                  value={value}
                  onChange={onChange}
                />
              )}
            />
          )}

          {brands?.length > 0 && (
            <div className="card card-with-border u-padding u-margin-bottom">
              <InputGroup title="Brand" className="u-margin-bottom--0">
                <Select
                  name="candidate.brand_id"
                  register={register}
                  value={brandId}
                  placeholder="Select Brand..."
                  useDefault>
                  {brands.map(brand => (
                    <Select.Item key={brand.id} value={brand.id}>
                      {brand.name}
                    </Select.Item>
                  ))}
                </Select>
              </InputGroup>
            </div>
          )}

          {documents.length > 0 && (
            <ToggleSwitchInput
              text="Attach documents"
              subtext="Select a document you wish to send to the candidate"
              onChange={e => setDocumentsEnabled(e.target.checked)}
              value={JSON.parse(documentsEnabled)}>
              {documentsEnabled && (
                <InputGroup title="Documents">
                  <Select
                    name="candidate.document_id"
                    placeholder="Document"
                    register={register}
                    value={documentId}>
                    {documents.map(document => (
                      <Select.Item key={document.id} value={document.id}>
                        {document.file_name}
                      </Select.Item>
                    ))}
                  </Select>
                </InputGroup>
              )}
            </ToggleSwitchInput>
          )}
        </div>

        <div className="border-t border-neutral-4" />
        <div className="p-6 pb-0 flex flex-col">
          {(!type || type === SelectorTypes.BACKGROUND_CHECKS) &&
            [...(companyChecks || [])]
              .filter(companyCheck => !companyCheck.check_type?.deprecated)
              .sort((a, b) => (a.check_type?.title || '').localeCompare(b.check_type?.title || ''))
              .map(companyCheck => renderDetails(companyCheck))}
          {type === SelectorTypes.REFERENCE && (
            <ReferenceSelectorDetails
              index={enabledChecks.reference}
              append={append}
              remove={remove}
              register={register}
              watch={watch}
              credits={
                companyChecks?.find(companyCheck => companyCheck.type === 'reference')?.credits
              }
              setValue={setValue}
              form={form}
              forms={forms}
              type={type}
              bulk={bulk}
              targetForm={targetForm}
              documents={documents}
              signUpload={signUpload}
              createUpload={createUpload}
              handleDeleteUpload={deleteUpload}
              allowUploads={allowUploads}
            />
          )}
          {type === SelectorTypes.CUSTOM_REFERENCE && (
            <CustomReferenceCheckDetails
              index={enabledChecks.custom_reference}
              append={append}
              remove={remove}
              control={control}
              register={register}
              watch={watch}
              setValue={setValue}
              documents={documents}
              credits={
                companyChecks?.find(companyCheck => companyCheck.type === 'custom_reference')
                  ?.credits
              }
              forms={forms}
              signUpload={signUpload}
              createUpload={createUpload}
              handleDeleteUpload={deleteUpload}
              allowUploads={allowUploads}
            />
          )}
          {type === SelectorTypes.QUESTIONNAIRE && (
            <CandidateQuestionnaireSelectorDetails
              index={enabledChecks.candidate_questionnaire}
              append={append}
              remove={remove}
              control={control}
              register={register}
              setValue={setValue}
              setError={setError}
              clearErrors={clearErrors}
              watch={watch}
              credits={
                companyChecks.find(companyCheck => companyCheck.type === 'candidate_questionnaire')
                  ?.credits
              }
              candidateForms={(forms.questionnaires || []).filter(form => !form.archived)}
            />
          )}
        </div>
      </form>
    );
  },
);

const SelectorDetails = ({
  company = null,
  companyCheck,
  enabledCompanyChecks = {},
  companyCheckConfigurations = {},
  candidateData = null,
  isExistingCheck = false,
  index,
  errors,
  control,
  append = () => {},
  remove = () => {},
  watch = () => {},
  register = () => {},
  setValue = () => {},
  getValues = () => {},
  bulk = false,
}) => {
  const checkType = companyCheck.check_type;
  const [enabled, setEnabled] = useState(false);

  // Merge form candidate (e.g. volunteer from bundle selection in step 2) so conditions and alternative_default see candidate.volunteer
  const formCandidate = useWatch({ control, name: 'candidate', defaultValue: {} });
  const candidate = useMemo(
    () => (candidateData || formCandidate ? { ...candidateData, ...formCandidate } : null),
    [candidateData, formCandidate],
  );

  // Sync enabled state with index (index is source of truth)
  // This must run first to ensure enabled is set before the append/remove effect runs
  const prevIndexRef = useRef(index);
  useEffect(() => {
    const indexChanged = prevIndexRef.current !== index;
    prevIndexRef.current = index;

    // Only update enabled if index actually changed
    if (!indexChanged) return;

    if (index >= 0 && !enabled) {
      setEnabled(true);
    } else if (isNaN(index) && enabled) {
      setEnabled(false);
    }
  }, [index]);

  // Append/remove check when enabled state changes
  // Only append/remove if enabled state actually changed (not just index)
  const prevEnabledRef = useRef(enabled);
  useEffect(() => {
    const enabledChanged = prevEnabledRef.current !== enabled;
    prevEnabledRef.current = enabled;

    if (!enabledChanged) return;

    if (enabled && isNaN(index)) {
      append({
        type: checkType.slug,
        details: {},
      });
    } else if (!enabled && index >= 0) {
      remove(index);
    }
  }, [enabled, index, append, remove, checkType.slug]);

  // State for hidden field elements - updated only when field count changes to avoid loops
  const [hiddenFieldElements, setHiddenFieldElements] = useState([]);
  const hiddenFieldCountRef = useRef(0);

  // Callback for hidden fields - only update state if count changes to avoid infinite loops
  const handleHiddenFieldsRender = useCallback(elements => {
    const newCount = elements?.length || 0;
    if (newCount !== hiddenFieldCountRef.current) {
      hiddenFieldCountRef.current = newCount;
      setHiddenFieldElements(elements);
    }
  }, []);

  // Check if there are any fields (visible or hidden)
  const hasFields = checkType.client_fields?.length > 0;

  const companyCheckConfig = companyCheck?.configuration ?? null;

  return (
    <>
      {/* Hidden fields: register with form outside ToggleSwitchInput to avoid triggering Collapse */}
      {enabled && index >= 0 && hiddenFieldElements}

      <ToggleSwitchInput
        text={checkType.title}
        icon={getCheckTypeIcon(checkType.slug)}
        className={companyCheck.credits === 0 ? 'u-border-error u-disabled' : ''}
        subtext={(companyCheck.credits < 0 ? 'Unlimited' : companyCheck.credits) + ' remaining'}
        toggle={!isExistingCheck}
        status={isExistingCheck && 'continue'}
        statusText={isExistingCheck && 'In Progress'}
        value={enabled}
        dataTestId={`toggle-switch-${checkType.slug}`}
        onChange={e => setEnabled(e.target.checked)}>
        {hasFields && enabled && index >= 0 && (
          <FieldConfigRenderer
            fields={checkType?.client_fields}
            control={control}
            register={register}
            setValue={setValue}
            getValues={getValues}
            watch={watch}
            formValuesPrefix={`checks.${index}.details`}
            errors={errors}
            company={company ?? null}
            enabledCompanyChecks={enabledCompanyChecks}
            companyCheckConfigurations={companyCheckConfigurations}
            companyCheckConfig={companyCheckConfig}
            candidate={candidate}
            candidateValues={null}
            checkDetails={null}
            subCheckDetails={null}
            onHiddenFieldsRender={handleHiddenFieldsRender}
            allowEmptyRequiredWhenDataSourceAlternative={!!bulk}
          />
        )}
      </ToggleSwitchInput>
    </>
  );
};
